import React from 'react'
import axios from 'axios'
import _ from 'lodash'
import { Dimmer, Loader } from 'semantic-ui-react'
import { Redirect } from 'react-router-dom'
import { BlueMoon, Dimple, handleError } from '../BlueMoon'
import { getTheme } from '../../config/clientConfig'
import { METRICS, SCOPES, STATS, buildModel } from './benchmarkMath'
import Picker from '../Common/Picker/Picker'
import Heatmap from './Heatmap'
import Ranking from './Ranking'
import './benchmark.css'

// 'YYYY-YYYY' => 'YYYY-YY'
const seasonText = season => season.slice(0, 5) + season.slice(-2);
const alpha = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * Partner Benchmark: each partner's season total per channel indexed against the channel median/mean
 * (port of benchmark-mock). One fetch per season; metric, scope, statistic and selection are client-side.
 */
class BenchmarkDashboard extends React.Component {
    constructor(props) {
        super(props);
        this.theme = getTheme();
        this.state = {
            data: null, model: null, season: undefined,
            partners: ['Toyota'], metric: 'meValue', scope: 'all', stat: 'mean',
            dimmer: true, error: null
        };
    }

    componentDidMount = () => {
        axios.defaults.headers.common = { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        this.fetchData();
    }

    // season undefined => backend default (latest season). Selected partners missing from the new season are dropped.
    fetchData = (season) => {
        this.setState({ dimmer: true, error: null, season }, () => {
            axios.get(`${BlueMoon.ip}dashboard/benchmark/`, {
                params: season ? { season } : {}
            }).then((response) => {
                const data = response.data;
                const model = buildModel(data.rows);
                this.setState((state) => ({
                    data, model, season: data.season, dimmer: false,
                    partners: state.partners.filter(name => model.cells.has(name))
                }));
            }).catch((err) => {
                handleError(err);
                this.setState((state) => ({
                    dimmer: false,
                    error: 'Benchmark data unavailable',
                    season: state.data ? state.data.season : undefined
                }));
            });
        });
    }

    seg = (id, label, options, value, field, className) => (
        <div className={className ? 'f ' + className : 'f'}>
            <span className="label" id={id}>{label}</span>
            <div className="seg" role="group" aria-labelledby={id}>
                {Object.entries(options).map(([key, text]) => (
                    <button type="button" key={key} className={key === value ? 'on' : undefined} aria-pressed={String(key === value)}
                        onClick={() => this.setState({ [field]: key })}>{text}</button>
                ))}
            </div>
        </div>
    )

    seasonPicker = (data) => (
        <div className="f">
            <Picker label="Season" multi={false} selected={[this.state.season]} onChange={values => this.fetchData(values[0])}
                options={(data.seasons || [data.season]).map(s => ({ value: s, text: seasonText(s) }))} />
        </div>
    )

    crest = (data) => (
        <header className="crest">
            <h1 className="brand">{this.theme.clientName} <span>· Partner Benchmark</span></h1>
            {data ? <div className="season">{seasonText(data.season)} Season · totals</div> : null}
        </header>
    )

    controls = (data) => {
        const { model, partners, metric, scope, stat } = this.state;
        const partnerOptions = model.partners.map(p => p.name).sort(alpha).map(name => ({ value: name, text: name }));
        return (
            <div className="controls" aria-label="Benchmark controls">
                <div className="filters">
                    <div className="f">
                        <Picker label="Partner" multi options={partnerOptions} selected={partners}
                            onChange={values => this.setState({ partners: values })} />
                    </div>
                    <div className="f">
                        <Picker label="Metric" multi={false} selected={[metric]} onChange={values => this.setState({ metric: values[0] })}
                            options={Object.entries(METRICS).map(([value, text]) => ({ value, text }))} />
                    </div>
                    {this.seasonPicker(data)}
                </div>
                {this.seg('benchmark-stat-label', 'Benchmark', STATS, stat, 'stat')}
                {this.seg('benchmark-scope-label', 'Benchmark scope', SCOPES, scope, 'scope', 'scope-control')}
            </div>
        );
    }

    content = (data) => {
        if (!data.rows.length) {
            return (
                <React.Fragment>
                    <div className="controls"><div className="filters">{this.seasonPicker(data)}</div></div>
                    <div className="empty">No benchmark data for the {seasonText(data.season)} season</div>
                </React.Fragment>
            );
        }
        const { model, partners, metric, scope, stat } = this.state;
        const view = { model, partners, metric, scope, stat };
        return (
            <React.Fragment>
                {this.controls(data)}
                <section id="benchmark-heatmap" aria-labelledby="benchmark-heatmap-title">
                    <h2 className="section-title" id="benchmark-heatmap-title">INDEX HEATMAP</h2>
                    <div className="view"><Heatmap {...view} /></div>
                </section>
                <details id="benchmark-ranking">
                    <summary><h2 className="section-title" id="benchmark-ranking-title">CHANNEL RANKING</h2></summary>
                    <div className="view"><Ranking {...view} /></div>
                </details>
            </React.Fragment>
        );
    }

    render() {
        // same entry guards as Dashboard; tenants without the flag go to the main dashboard
        if (_.isEmpty(localStorage.getItem('user'))) window.location.replace(`${Dimple}#/login`);
        if (_.isEmpty(localStorage.getItem('projectIp'))) window.location.replace(`${Dimple}#/home`);
        if (_.isEmpty(localStorage.getItem('user'))) return null;
        if (!(this.theme.features && this.theme.features.benchmark)) return <Redirect to="/dashboard/" />;

        const { data, dimmer, error } = this.state;
        return (
            <div className="benchmark-page">
                <div className="benchmark">
                    {this.crest(data)}
                    {error ? <div className="empty error">{error}</div> : null}
                    {data ? this.content(data) : null}
                    <Dimmer active={dimmer}><Loader /></Dimmer>
                </div>
            </div>
        );
    }
}

export default BenchmarkDashboard;
